import { useEffect, useState } from "react";
import { ApiError } from "@/api/client";
import {
  useChangeEmail,
  useChangePassword,
  useMyDocuments,
  useSetMyGender,
  useUpdateMe,
  useUpdateWorkMode,
} from "@/api/queries";
import { roleLabel, useSession } from "@/api/session";
import type { Gender, WorkMode } from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, LabelledInput, Select } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { toast } from "@/components/ui/toast";
import { useTheme } from "@/lib/theme";
import { WORK_MODE_LABEL } from "@/lib/labels";

export function AccountSettingsPage() {
  const user = useSession((s) => s.user);
  const setUser = useSession((s) => s.setUser);
  const adoptToken = useSession((s) => s.adoptToken);
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);

  const documents = useMyDocuments();
  const updateMe = useUpdateMe();
  const changeEmail = useChangeEmail();
  const changePassword = useChangePassword();
  const updateWorkMode = useUpdateWorkMode();
  const setGender = useSetMyGender();

  const [name, setName] = useState(user?.name ?? "");
  const [designation, setDesignation] = useState(user?.designation ?? "");
  /*
    Work mode is DERIVED from the session user, never seeded into state.

    It used to be `useState(user?.work_mode ?? "onsite")`, read from a copy of
    the account written at login and never refreshed — so HR switching somebody
    to remote left this page showing, and submitting, "Onsite" until that person
    logged out and back in. App.tsx now re-reads the account from the server, and
    reading it here rather than copying it is what lets that refresh land.
  */
  const workMode = user?.work_mode;
  const [gender, setGenderValue] = useState<Gender | "">("");

  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  async function saveEmail() {
    setEmailError(null);
    const next = newEmail.trim().toLowerCase();
    if (!next) {
      setEmailError("Enter the new email address.");
      return;
    }
    if (!emailPassword) {
      setEmailError("Enter your current password to confirm this change.");
      return;
    }
    try {
      const result = await changeEmail.mutateAsync({ currentPassword: emailPassword, email: next });
      // Email is inside the signed token, so the old one is stale immediately.
      adoptToken(result.token);
      if (user) setUser({ ...user, email: result.email });
      setNewEmail("");
      setEmailPassword("");
      toast.success(`You'll sign in with ${result.email} from now on.`);
    } catch (err) {
      setEmailError(err instanceof ApiError ? err.message : "Could not change your email.");
    }
  }

  useEffect(() => {
    const savedGender = documents.data?.profile.gender;
    if (savedGender) setGenderValue((current) => current || savedGender);
  }, [documents.data?.profile.gender]);

  async function saveProfile() {
    try {
      const result = await updateMe.mutateAsync({ name: name.trim(), designation: designation.trim() });
      setUser(result.user);
      toast.success("Your details were updated.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not update your details.");
    }
  }

  async function saveWorkMode(next: WorkMode) {
    try {
      await updateWorkMode.mutateAsync(next);
      // The field re-renders from this, so what it shows is what the server
      // accepted — there is no local copy left to roll back on failure.
      if (user) setUser({ ...user, work_mode: next });
      toast.success(`Work mode set to ${WORK_MODE_LABEL[next].toLowerCase()}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not change your work mode.");
    }
  }

  async function saveGender(next: Gender) {
    setGenderValue(next);
    try {
      await setGender.mutateAsync(next);
      toast.success("Gender updated. Leave eligibility is recalculated from this.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not update that.");
    }
  }

  async function savePassword() {
    setPasswordError(null);
    if (newPassword.length < 8) {
      setPasswordError("Use at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("The two new passwords do not match.");
      return;
    }
    try {
      const result = await changePassword.mutateAsync({ currentPassword, newPassword });
      // The backend reissues a token; adopting it keeps the session valid.
      adoptToken(result.token);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password updated.");
    } catch (err) {
      setPasswordError(err instanceof ApiError ? err.message : "Could not change your password.");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Your account"
        title="Account settings"
        sub="Change your own details, work mode, appearance and password."
      />

      <Panel>
        <PanelHeader
          title="Your details"
          note="Name and designation are shown wherever you appear across the system."
        />
        <div className="flex flex-col gap-4 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <LabelledInput label="Full name" value={name} onChange={(e) => setName(e.target.value)} />
            <LabelledInput
              label="Designation"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Email"
              htmlFor="acct-email"
              hint="This is your login. Change it under Login email below."
            >
              <input
                id="acct-email"
                readOnly
                value={user?.email ?? ""}
                className="h-9 w-full cursor-not-allowed rounded-lg border border-line bg-surface-sunken px-3 font-mono text-sm text-muted"
              />
            </Field>
            <Field
              label="Team"
              htmlFor="acct-team"
              hint={
                user?.role === "admin"
                  ? "The HR and Admin account is not mapped to a team; it spans the whole organisation."
                  : "Mapped by HR and Admin."
              }
            >
              <input
                id="acct-team"
                readOnly
                value={user?.team_name ?? "Not mapped"}
                className="h-9 w-full cursor-not-allowed rounded-lg border border-line bg-surface-sunken px-3 text-sm text-muted"
              />
            </Field>
          </div>
          <div>
            <Button variant="primary" iconLeft="save" onClick={saveProfile} loading={updateMe.isPending}>
              Save details
            </Button>
          </div>
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel className="min-w-0">
          <PanelHeader
            title="Work mode"
            note="Decides whether you check in and out, or mark yourself active and inactive."
          />
          <div className="flex flex-col gap-3 p-4">
            <Field label="How you work" htmlFor="acct-mode">
              <Select
                id="acct-mode"
                value={workMode ?? ""}
                disabled={!workMode || updateWorkMode.isPending}
                onChange={(e) => void saveWorkMode(e.target.value as WorkMode)}
              >
                {/* Until the account is known there is no honest value to show,
                    and an empty disabled select says that better than a guess. */}
                {!workMode && <option value="">Loading…</option>}
                <option value="onsite">Onsite</option>
                <option value="hybrid">Hybrid</option>
                <option value="remote">Remote</option>
              </Select>
            </Field>
            <p className="text-xs leading-relaxed text-muted">
              Onsite records real check-in and check-out times. Remote marks you active then
              inactive. Hybrid lets you pick per day, and the first action locks that day's mode.
            </p>
          </div>
        </Panel>

        <Panel className="min-w-0">
          <PanelHeader title="Gender" note="Drives Menstrual Leave eligibility. Nothing else reads it." />
          <div className="flex flex-col gap-3 p-4">
            <Field label="Gender" htmlFor="acct-gender">
              <Select
                id="acct-gender"
                value={gender}
                onChange={(e) => void saveGender(e.target.value as Gender)}
              >
                <option value="">Not set</option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
                <option value="Other">Other</option>
              </Select>
            </Field>
            <p className="text-xs leading-relaxed text-muted">
              While this is unset the benefit stays visible, so nothing is hidden from you by
              default.
            </p>
          </div>
        </Panel>
      </div>

      <Panel>
        <PanelHeader title="Appearance" note="Applies to this browser only." />
        <div className="flex flex-wrap items-center gap-3 p-4">
          {(["light", "dark"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setTheme(option)}
              aria-pressed={theme === option}
              className={
                "flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors active:scale-[0.98] " +
                (theme === option
                  ? "border-accent bg-accent-wash text-accent"
                  : "border-line text-ink-soft hover:bg-surface-sunken")
              }
            >
              <Icon name={option === "light" ? "sun" : "moon"} size={20} />
              {option === "light" ? "Light" : "Dark"}
              {theme === option && <Icon name="check" size={16} />}
            </button>
          ))}
        </div>
      </Panel>

      <Panel>
        <PanelHeader
          title="Login email"
          note="The address you sign in with. Changing it requires your current password, because it decides who can get into this account."
        />
        <div className="flex flex-col gap-4 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <LabelledInput
              label="New email"
              type="email"
              autoComplete="email"
              placeholder={user?.email ?? "you@mentoria.com"}
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
            <LabelledInput
              label="Current password"
              type="password"
              autoComplete="current-password"
              error={emailError}
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
            />
          </div>
          <div>
            <Button
              variant="primary"
              iconLeft="email"
              onClick={saveEmail}
              loading={changeEmail.isPending}
            >
              Change email
            </Button>
          </div>
        </div>
      </Panel>

      <Panel>
        <PanelHeader
          title="Password"
          note="Changing your password keeps you signed in on this device."
        />
        <div className="flex flex-col gap-4 p-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <LabelledInput
              label="Current password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <LabelledInput
              label="New password"
              type="password"
              autoComplete="new-password"
              hint="At least 8 characters."
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <LabelledInput
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              error={passwordError}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              iconLeft="password"
              onClick={savePassword}
              loading={changePassword.isPending}
            >
              Change password
            </Button>
            <Badge tone="outline" icon="lock">
              {/* The shared helper, not a second copy of the same ladder: this one
                  had no "Super Admin Account" branch, so a People-Function
                  approver was badged differently here than on the accounts page,
                  and a null user read as "Employee". */}
              {user ? roleLabel(user) : "—"}
            </Badge>
          </div>
        </div>
      </Panel>
    </div>
  );
}
